/**
 * La linea de tiempo del stock: cada ingreso y cada salida, con lo que quedo.
 *
 * Existe porque el cliente corregia contando dos productos, guardaba, y
 * preguntaba donde habia quedado eso. Se registraba, pero no se veia.
 *
 * Tres piezas:
 *   - `TablaMovimientos`: la tabla pura.
 *   - `PanelMovimientos`: tabla + filtros + aviso de diferencia + "Ver mas".
 *     No sabe de red: recibe todo por props, y es lo que se testea.
 *   - `DialogMovimientos`: el dialogo que consulta y acumula paginas.
 *
 * DOS MODOS. Con producto es el historial de ESE producto, y compara el
 * ultimo acumulado contra `stock_actual`. Sin producto (`modoGlobal`) es el
 * diario de todo el local: aparece la columna del producto, un buscador y el
 * filtro por sentido, y no hay comparacion porque no hay un stock que
 * comparar.
 *
 * EL AVISO DE DIFERENCIA. El acumulado de la vista arranca en cero y suma
 * movimientos. Si el ultimo no coincide con `stock_actual`, hay unidades sin
 * rastro. No se esconde ni se arregla desde aca: se dice, y se manda a
 * «Corregir contando», que deja el ajuste firmado.
 */
import { useEffect, useMemo, useState } from "react";
import { formatearNumero } from "@tora/shared";

import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import { Cargando } from "@/components/ui/cargando";
import { Codigo } from "@/components/ui/codigo";
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableCellImporte,
  TableHead,
  TableHeader,
  TableRow,
  TableVacia,
} from "@/components/ui/table";
import { useMovimientosStock } from "@/lib/queries";
import {
  aNumero,
  type FiltrosMovimientos,
  type MovimientoStock,
  type Numerico,
  type Producto,
  type SentidoMovimiento,
} from "@/lib/tipos";
import { cn, formatearFechaHora } from "@/lib/utils";

import { nombreCompleto } from "./nombre";
import { describirMovimiento, diferenciaConStock, ordenarMovimientos } from "./logica";

const POR_PAGINA = 50;

/** "5" en vez de "5,00": las unidades enteras se leen sin decimales. */
function unidades(n: Numerico): string {
  return formatearNumero(aNumero(n), 2).replace(/,00$/, "");
}

function conSigno(delta: Numerico): string {
  const n = aNumero(delta);
  return `${n > 0 ? "+" : ""}${unidades(n)}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// La tabla
// ─────────────────────────────────────────────────────────────────────────────

export interface TablaMovimientosProps {
  filas: ReadonlyArray<MovimientoStock>;
  cargando?: boolean;
  /** En el diario de todo el local se muestra de que producto es cada fila. */
  modoGlobal: boolean;
}

export function TablaMovimientos({
  filas,
  cargando = false,
  modoGlobal,
}: TablaMovimientosProps): JSX.Element {
  const columnas = modoGlobal ? 6 : 5;

  return (
    <Table densidad="compacta" alto="acotado">
      <TableHeader>
        <TableRow>
          <TableHead>Fecha</TableHead>
          {modoGlobal ? <TableHead>Producto</TableHead> : null}
          <TableHead>Qué pasó</TableHead>
          <TableHead className="text-right">Cantidad</TableHead>
          <TableHead className="text-right">Quedó</TableHead>
          <TableHead>Quién</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {/* Mientras busca no hay "nada": la espera se dice como espera, no
            como un vacio que despues cambia de texto. */}
        {filas.length === 0 && cargando ? (
          <tr>
            <td colSpan={columnas}>
              <Cargando texto="Buscando…" />
            </td>
          </tr>
        ) : filas.length === 0 ? (
          <TableVacia colSpan={columnas} mensaje="Todavía no hay movimientos." />
        ) : (
          filas.map((m) => {
            const entra = m.sentido === "INGRESO";
            const detalle = [m.referencia, m.observaciones].filter(Boolean).join(" · ");
            return (
              <TableRow key={m.clave}>
                <TableCell className="whitespace-nowrap text-muted-foreground">
                  {formatearFechaHora(m.fecha)}
                </TableCell>
                {modoGlobal ? (
                  <TableCell>
                    <Codigo className="text-xs text-muted-foreground">{m.codigo_interno}</Codigo>{" "}
                    <span>{nombreCompleto({ ...m, nombre: m.producto })}</span>
                  </TableCell>
                ) : null}
                <TableCell>
                  <div>{describirMovimiento(m)}</div>
                  {detalle ? <div className="text-xs text-muted-foreground">{detalle}</div> : null}
                </TableCell>
                <TableCellImporte
                  className={cn("font-semibold", entra ? "text-success" : "text-destructive")}
                >
                  {conSigno(m.delta)}
                </TableCellImporte>
                <TableCellImporte>{unidades(m.stock_despues)}</TableCellImporte>
                <TableCell className="text-muted-foreground">{m.usuario ?? ""}</TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// El panel: filtros, aviso, tabla, ver mas
// ─────────────────────────────────────────────────────────────────────────────

export interface FiltrosMovimientosUi {
  texto: string;
  sentido: SentidoMovimiento | "";
}

export const FILTROS_MOVIMIENTOS_INICIALES: FiltrosMovimientosUi = { texto: "", sentido: "" };

export interface PanelMovimientosProps {
  filas: ReadonlyArray<MovimientoStock>;
  total: number;
  cargando: boolean;
  /** El stock del producto, para el aviso. `null` si no se conoce o en modo global. */
  stockActual: Numerico | null;
  modoGlobal: boolean;
  filtros: FiltrosMovimientosUi;
  onFiltros: (f: FiltrosMovimientosUi) => void;
  onVerMas: () => void;
}

export function PanelMovimientos({
  filas,
  total,
  cargando,
  stockActual,
  modoGlobal,
  filtros,
  onFiltros,
  onVerMas,
}: PanelMovimientosProps): JSX.Element {
  const quedanMas = filas.length < total;
  const ordenadas = useMemo(() => ordenarMovimientos(filas), [filas]);
  const diferencia = modoGlobal ? null : diferenciaConStock(ordenadas, stockActual);

  return (
    <div className="space-y-4">
      {modoGlobal ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          <div className="space-y-1.5">
            <Label htmlFor="mov-buscar">Producto</Label>
            <Input
              id="mov-buscar"
              type="search"
              value={filtros.texto}
              onChange={(e) => onFiltros({ ...filtros, texto: e.target.value })}
              placeholder="Nombre o código"
              autoComplete="off"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="mov-sentido">Sentido</Label>
            <Select
              value={filtros.sentido || "_"}
              onValueChange={(v) =>
                onFiltros({ ...filtros, sentido: v === "_" ? "" : (v as SentidoMovimiento) })
              }
            >
              <SelectTrigger id="mov-sentido" aria-label="Sentido">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_">Todos</SelectItem>
                <SelectItem value="INGRESO">Ingresos</SelectItem>
                <SelectItem value="SALIDA">Salidas</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      ) : null}

      {/* `role="alert"` a proposito aunque sea warning: unidades sin rastro
          se anuncian, no se esperan. */}
      {diferencia !== null && stockActual !== null ? (
        <Aviso tono="warning" role="alert">
          El sistema dice {unidades(stockActual)} pero los movimientos suman{" "}
          {unidades(ordenadas[0]?.stock_despues ?? 0)}. La diferencia de{" "}
          {unidades(Math.abs(diferencia))} no tiene rastro: se corrige con «Corregir contando».
        </Aviso>
      ) : null}

      <TablaMovimientos filas={ordenadas} cargando={cargando} modoGlobal={modoGlobal} />

      {quedanMas ? (
        <div className="flex justify-center">
          <Button type="button" variant="outline" size="sm" onClick={onVerMas} disabled={cargando}>
            Ver más
          </Button>
        </div>
      ) : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// El panel con datos: consulta, acumula paginas
// ─────────────────────────────────────────────────────────────────────────────

export interface MovimientosStockProps {
  /** Con producto es su historial. Sin producto, el diario de todo el local. */
  producto: Producto | null;
}

/**
 * `PanelMovimientos` enchufado a la API. Es lo que Inventario muestra en su pestaña
 * «Movimientos» y lo que el dialogo de la ficha envuelve.
 *
 * Los filtros y las paginas viven aca y se reinician al cambiar de producto:
 * un filtro que quedo de la vez anterior esconde movimientos sin que se note.
 */
export function MovimientosStock({ producto }: MovimientosStockProps): JSX.Element {
  const modoGlobal = producto === null;
  const [filtros, setFiltros] = useState<FiltrosMovimientosUi>(FILTROS_MOVIMIENTOS_INICIALES);
  const [paginas, setPaginas] = useState(1);

  useEffect(() => {
    setFiltros(FILTROS_MOVIMIENTOS_INICIALES);
    setPaginas(1);
  }, [producto]);

  const consulta = useMemo<FiltrosMovimientos>(
    () => ({
      ...(producto ? { producto_id: producto.id } : {}),
      ...(filtros.sentido ? { sentido: filtros.sentido } : {}),
      ...(filtros.texto.trim() ? { q: filtros.texto.trim() } : {}),
      // "Ver mas" pide mas filas desde cero en una sola consulta, en vez de
      // pegar paginas que pueden haber cambiado entre medio.
      limite: POR_PAGINA * paginas,
      offset: 0,
    }),
    [producto, filtros, paginas],
  );

  const { data, isFetching } = useMovimientosStock(consulta);

  function cambiarFiltros(f: FiltrosMovimientosUi): void {
    setFiltros(f);
    setPaginas(1);
  }

  return (
    <PanelMovimientos
      filas={data?.filas ?? []}
      total={data?.total ?? 0}
      cargando={isFetching && !data}
      stockActual={producto ? (data?.stock_actual ?? producto.stock_actual) : null}
      modoGlobal={modoGlobal}
      filtros={filtros}
      onFiltros={cambiarFiltros}
      onVerMas={() => setPaginas((p) => p + 1)}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// El dialogo: el mismo panel, desde la ficha del producto
// ─────────────────────────────────────────────────────────────────────────────

export interface DialogMovimientosProps {
  abierto: boolean;
  onAbiertoChange: (abierto: boolean) => void;
  producto: Producto | null;
}

export function DialogMovimientos({
  abierto,
  onAbiertoChange,
  producto,
}: DialogMovimientosProps): JSX.Element {
  return (
    <Dialog open={abierto} onOpenChange={onAbiertoChange}>
      <DialogContent tamano="lg">
        <DialogHeader>
          <DialogTitle>
            {producto ? `Movimientos de ${nombreCompleto(producto)}` : "Movimientos de stock"}
          </DialogTitle>
          <DialogDescription>
            {producto
              ? `Hoy hay ${unidades(producto.stock_actual)} u. Cada fila dice qué pasó y cuánto quedó después.`
              : "Todo lo que entró y salió, de lo más nuevo a lo más viejo."}
          </DialogDescription>
        </DialogHeader>

        {/* Se monta solo abierto: asi los filtros arrancan limpios cada vez. */}
        <DialogBody>{abierto ? <MovimientosStock producto={producto} /> : null}</DialogBody>

        {/* Solo se mira: la unica accion es irse. */}
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Cerrar
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
